import { CONFIG } from '@/config/environment';
import { lerAccessToken, lerRefreshToken } from '@/security/tokens';
import { refreshSession } from '@/security/refreshSession';
import { guardarJSON } from '@/utils/storageSeguro';

// O prefixo protegido sobrevive a "Novo Jogo" e às limpezas de estados antigos.
const PREFIXO = 'padelmaster_resultado_';
export const EVENTO_RESULTADOS = 'padelmaster-resultados-alterados';
export interface Confirmacao { success: true; game_id: number; stats_errors?: unknown[] }
export type EstadoEnvio = Confirmacao | { success: false; pending: true };
interface Entrada {
  owner: string;
  body: string;
  finishedAt: string;
  estado: 'pendente' | 'erro' | 'confirmado';
  erro?: string;
  result?: Confirmacao;
}

// O JWT só identifica a fila local. A autorização é sempre validada no servidor.
export function contaDosResultados(): string | null {
  for (const token of [lerAccessToken(), lerRefreshToken()]) {
    try {
      const payload = JSON.parse(atob(token!.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (payload.user_id != null) return String(payload.user_id);
    } catch { /* sem sessão guardada */ }
  }
  return null;
}
const notificar = () => window.dispatchEvent(new Event(EVENTO_RESULTADOS));
function ler(chave: string): Entrada | null {
  const raw = localStorage.getItem(chave);
  return raw ? JSON.parse(raw) as Entrada : null;
}
function gravar(chave: string, entrada: Entrada): void {
  if (!guardarJSON(chave, entrada)) {
    throw new Error('A memória do dispositivo está cheia ou indisponível. Não foi possível guardar os resultados para envio. Não inicies outro jogo.');
  }
  notificar();
}
function entradas(): [string, Entrada][] {
  const lista: [string, Entrada][] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const chave = localStorage.key(i);
    if (!chave?.startsWith(PREFIXO)) continue;
    try { const entrada = ler(chave); if (entrada) lista.push([chave, entrada]); }
    catch { /* nunca apagar uma entrada que não conseguimos ler */ }
  }
  return lista;
}
export function contarResultadosPendentes(): number {
  const owner = contaDosResultados();
  try { return entradas().filter(([, e]) => e.owner === owner && e.estado !== 'confirmado').length; }
  catch { return 0; }
}

const emCurso = new Map<string, Promise<EstadoEnvio>>();
async function exclusivo(chave: string, executar: () => Promise<EstadoEnvio>): Promise<EstadoEnvio> {
  const anterior = emCurso.get(chave);
  if (anterior) { await anterior.catch(() => {}); return exclusivo(chave, executar); }
  const promessa = Promise.resolve().then(async () => navigator.locks
    ? await navigator.locks.request(chave, executar) : await executar());
  emCurso.set(chave, promessa);
  try { return await promessa; }
  finally { emCurso.delete(chave); }
}
class ErroHTTP extends Error {
  constructor(public status: number) { super(`Servidor respondeu ${status}.`); }
}
async function enviar(chave: string, entrada: Entrada): Promise<EstadoEnvio> {
  if (navigator.onLine === false) return { success: false, pending: true };
  try {
    if (contaDosResultados() !== entrada.owner) return { success: false, pending: true };
    let token = lerAccessToken();
    if (!token) token = (await refreshSession()).access;
    const pedido = (access: string) => {
      if (contaDosResultados() !== entrada.owner) throw new Error('A sessão mudou.');
      return fetch(`${CONFIG.BACKEND_URL}/padel/game/finalize/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${access}` },
        body: JSON.stringify({ ...JSON.parse(entrada.body), finished_at: entrada.finishedAt }),
        signal: AbortSignal.timeout(30000),
      });
    };
    let response = await pedido(token);
    if (response.status === 401) response = await pedido((await refreshSession()).access);
    if (!response.ok) throw new ErroHTTP(response.status);
    const result = await response.json() as Confirmacao;
    if (result.success !== true || !result.game_id) throw new Error('O servidor não confirmou a gravação.');
    // Guardar a confirmação, em vez de apagar, evita reenviar ao reabrir o PDF.
    gravar(chave, { ...entrada, estado: 'confirmado', result, erro: undefined });
    // Nunca eliminar resultados pendentes por idade ou quantidade.
    const antigos = entradas().filter(([, e]) => e.estado === 'confirmado')
      .sort((a, b) => b[1].finishedAt.localeCompare(a[1].finishedAt)).slice(30);
    for (const [key] of antigos) localStorage.removeItem(key);
    return result;
  } catch (error) {
    const permanente = error instanceof ErroHTTP && [400, 403, 404, 422].includes(error.status);
    gravar(chave, { ...entrada, estado: permanente ? 'erro' : 'pendente', erro: error instanceof Error ? error.message : String(error) });
    if (permanente) throw error;
    return { success: false, pending: true };
  }
}

export async function enviarResultadoComFila(gameNumber: number, body: string): Promise<EstadoEnvio> {
  const owner = contaDosResultados();
  if (!owner) throw new Error('Inicia sessão antes de guardar resultados neste dispositivo.');
  const chave = `${PREFIXO}${owner}_${gameNumber}`;
  return exclusivo(chave, async () => {
    const anterior = ler(chave);
    if (anterior?.estado === 'confirmado' && anterior.body === body && anterior.result) return anterior.result;
    const entrada: Entrada = { owner, body, finishedAt: anterior?.finishedAt || new Date().toISOString(), estado: 'pendente' };
    // Escrever ANTES da rede: fechar a app ou perder a resposta não perde o jogo.
    gravar(chave, entrada);
    return enviar(chave, entrada);
  });
}

let sincronizacao: Promise<void> | null = null;
export function sincronizarResultados(incluirErros = false): Promise<void> {
  if (sincronizacao) return sincronizacao;
  sincronizacao = (async () => {
    const owner = contaDosResultados();
    if (!owner || navigator.onLine === false) return;
    for (const [chave, entrada] of entradas()) {
      if (entrada.owner !== owner || entrada.estado === 'confirmado' || (entrada.estado === 'erro' && !incluirErros)) continue;
      await exclusivo(chave, async () => {
        const atual = ler(chave);
        if (!atual || atual.estado === 'confirmado') return atual?.result || { success: false, pending: true };
        return enviar(chave, atual);
      }).catch(() => { /* fica na fila; o aviso permanece visível */ });
    }
  })().finally(() => { sincronizacao = null; notificar(); });
  return sincronizacao;
}

export function iniciarFilaResultados(): () => void {
  const tentar = () => { void sincronizarResultados().catch(() => {}); };
  const visivel = () => { if (document.visibilityState === 'visible') tentar(); };
  window.addEventListener('online', tentar);
  document.addEventListener('visibilitychange', visivel);
  const timer = window.setInterval(tentar, 60000);
  tentar();
  return () => {
    window.removeEventListener('online', tentar);
    document.removeEventListener('visibilitychange', visivel);
    window.clearInterval(timer);
  };
}
